import React, { useState, useEffect, useMemo, useContext } from "react";
import { UserContext } from "../../App";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import * as XLSX from "xlsx";

function MailTracker({ isSidebarOpen }) {
  const { handleAccess, setAuthPop } = useContext(UserContext);
  const [mailData, setMailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("mail_level");
  const [orderBy, setOrderBy] = useState("sent_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchMailTrackerData();
  }, []);

  const fetchMailTrackerData = async () => {
    try {
      setLoading(true);
      const { REACT_APP_GET_MAIL_TRACKER_DATA } = process.env;

      if (!REACT_APP_GET_MAIL_TRACKER_DATA) {
        console.error("REACT_APP_GET_MAIL_TRACKER_DATA is not defined");
        return;
      }

      const response = await axios.get(REACT_APP_GET_MAIL_TRACKER_DATA);

      if (response.status === 200) {
        setMailData(response.data?.data || response.data || []);
      }
    } catch (err) {
      console.error("Mail Tracker API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Summary calculations
  const totalSent = mailData.length;
  const viewedCount = mailData.filter((d) => d.status === "Viewed").length;
  const notViewedCount = mailData.filter((d) => d.status === "Not Viewed").length;
  const invoiceCount = mailData.filter((d) => d.mail_type === "invoice").length;

  // Filter by type and search (status filter applied after grouping)
  const baseFilteredData = mailData.filter((row) => {
    const matchesType = typeFilter === "all" || row.mail_type === typeFilter;
    const matchesState = stateFilter === "all" || row.state === stateFilter;
    const searchLower = search.toLowerCase();
    const matchesSearch =
      !search ||
      row.client_name?.toLowerCase().includes(searchLower) ||
      row.subject?.toLowerCase().includes(searchLower) ||
      row.mail_id?.toLowerCase().includes(searchLower) ||
      row.unique_id?.toLowerCase().includes(searchLower);
    return matchesType && matchesState && matchesSearch;
  });

  // Client-level grouping: group by client_name, aggregate status
  const clientLevelData = (() => {
    const grouped = {};
    baseFilteredData.forEach((row) => {
      const key = row.client_name || "-";
      if (!grouped[key]) {
        grouped[key] = {
          client_name: row.client_name,
          unique_id: row.unique_id,
          subject: row.subject,
          mail_type: row.mail_type,
          status: "Not Viewed",
        };
      }
      if (row.status === "Viewed") {
        grouped[key].status = "Viewed";
      }
    });
    return Object.values(grouped);
  })();

  // Apply status filter after grouping so it works on aggregated client status
  const applyStatusFilter = (data) => {
    if (statusFilter === "all") return data;
    return data.filter((row) => row.status === statusFilter);
  };

  const activeData = applyStatusFilter(
    levelFilter === "client_level" ? clientLevelData : baseFilteredData
  );

  // Sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = [...activeData].sort((a, b) => {
    let aVal = a[orderBy] || "";
    let bVal = b[orderBy] || "";

    if (orderBy === "sent_at") {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    } else {
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();
    }

    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
      ", " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
  };

  const typeDisplayMap = {
    invoice: "Invoice",
    credit_note: "Credit Note",
    debit_note: "Debit Note",
  };

  const formatTypeLabel = (type) => {
    if (!type) return "-";
    if (typeDisplayMap[type]) return typeDisplayMap[type];
    return String(type)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Build a stable color map for every unique mail_type in the dataset
  const TYPE_PALETTE = [
    { bg: "#dbeafe", color: "#1e40af" }, // blue
    { bg: "#dcfce7", color: "#166534" }, // green
    { bg: "#fee2e2", color: "#991b1b" }, // red
    { bg: "#fef3c7", color: "#92400e" }, // amber
    { bg: "#e0e7ff", color: "#3730a3" }, // indigo
    { bg: "#fce7f3", color: "#9d174d" }, // pink
    { bg: "#cffafe", color: "#155e75" }, // cyan
    { bg: "#ede9fe", color: "#5b21b6" }, // violet
    { bg: "#fef9c3", color: "#854d0e" }, // yellow
    { bg: "#fae8ff", color: "#86198f" }, // fuchsia
  ];

  const uniqueMailTypes = useMemo(() => {
    return Array.from(
      new Set(mailData.map((d) => d.mail_type).filter(Boolean))
    ).sort();
  }, [mailData]);

  const typeColorMap = useMemo(() => {
    // Pin known core types to consistent colors first
    const map = {
      invoice: TYPE_PALETTE[0],
      credit_note: TYPE_PALETTE[1],
      debit_note: TYPE_PALETTE[2],
    };
    let idx = 3;
    uniqueMailTypes.forEach((t) => {
      if (!map[t]) {
        map[t] = TYPE_PALETTE[idx % TYPE_PALETTE.length];
        idx += 1;
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueMailTypes]);

  const getTypeBadge = (type) => {
    const style = typeColorMap[type] || { bg: "#f1f5f9", color: "#475569" };
    return (
      <span
        style={{
          backgroundColor: style.bg,
          color: style.color,
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "11px",
          fontWeight: 600,
        }}
      >
        {formatTypeLabel(type)}
      </span>
    );
  };

  const getStatusText = (status) => {
    const isViewed = status === "Viewed";
    return (
      <span
        style={{
          color: isViewed ? "#16a34a" : "#dc2626",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {status}
      </span>
    );
  };

  const handleExportExcel = () => {
    const exportData = sortedData.map((row) => {
      const base = {
        "Client ID": row.unique_id || "-",
        "Client Name": row.client_name || "-",
        "Type": typeDisplayMap[row.mail_type] || row.mail_type || "-",
        "Subject": row.subject || "-",
      };
      if (levelFilter === "mail_level") {
        base["Mail ID"] = row.mail_id || "-";
        base["Sent At"] = row.sent_at ? formatDate(row.sent_at) : "-";
      }
      base["Status"] = row.status || "-";
      if (levelFilter === "mail_level") {
        base["Mail State"] = row.state || "-";
      }
      return base;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mail Tracker");
    XLSX.writeFile(wb, `Mail_Tracker_${levelFilter}.xlsx`);
  };

  const lastUpdatedAt = (() => {
    let max = 0;
    mailData.forEach((d) => {
      const t = d?.updated_at ? new Date(d.updated_at).getTime() : 0;
      if (t > max) max = t;
    });
    if (!max) return "-";
    const date = new Date(max);
    return (
      date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      ", " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  })();

  const summaryCards = [
    { label: "Total Sent", value: totalSent, bg: "#eef2ff", color: "#3b82f6" },
    { label: "Viewed", value: viewedCount, bg: "#ecfdf5", color: "#16a34a" },
    { label: "Not Viewed", value: notViewedCount, bg: "#fff7ed", color: "#ea580c" },
    { label: "Invoices", value: invoiceCount, bg: "#f5f3ff", color: "#7c3aed" },
    { label: "Last Updated", value: lastUpdatedAt, bg: "#f1f5f9", color: "#475569" },
  ];

  return (
    <div className="mail-tracker-container">
      {/* Single row: Summary left + Filters right */}
      <div className="mail-tracker-toolbar">
        <div className="mail-tracker-summary">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="mail-tracker-card"
              style={{ backgroundColor: card.bg }}
            >
              <span className="mail-tracker-card-label">{card.label}</span>
              <span
                className="mail-tracker-card-value"
                style={{ color: card.color, fontSize: card.label === "Last Updated" ? "12px" : undefined }}
              >
                {card.value}
              </span>
            </div>
          ))}
        </div>
        <div className="mail-tracker-filters">
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon style={{ fontSize: "16px" }} />}
            onClick={() => {
              if (handleAccess("MailTracker", "Export")) {
                handleExportExcel();
              } else {
                setAuthPop(true);
              }
            }}
            disabled={sortedData.length === 0}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              fontSize: "11px",
              padding: "4px 10px",
              backgroundColor: "#3949ab",
              "&:hover": { backgroundColor: "#303f9f" },
            }}
          >
            Export
          </Button>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: "6px", fontSize: "11px" }}
            >
              <MenuItem value="mail_level">Mail Level</MenuItem>
              <MenuItem value="client_level">Client Level</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder="Search..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#94a3b8", fontSize: "16px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "11px",
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 95 }}>
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: "6px", fontSize: "11px" }}
            >
              <MenuItem value="all">All Types</MenuItem>
              {uniqueMailTypes.map((t) => (
                <MenuItem key={t} value={t}>{formatTypeLabel(t)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: "6px", fontSize: "11px" }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="Viewed">Viewed</MenuItem>
              <MenuItem value="Not Viewed">Not Viewed</MenuItem>
            </Select>
          </FormControl>
          {levelFilter === "mail_level" && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: "6px", fontSize: "11px" }}
              >
                <MenuItem value="all">All States</MenuItem>
                {[...new Set(mailData.map((d) => d.state).filter(Boolean))].sort().map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <CircularProgress />
        </div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            borderRadius: "8px",
            maxHeight: "calc(100vh - 265px)",
            overflow: "auto",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>Client ID</TableCell>
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={orderBy === "client_name"}
                    direction={orderBy === "client_name" ? order : "asc"}
                    onClick={() => handleSort("client_name")}
                    sx={sortLabelStyle}
                  >
                    Client Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, minWidth: "120px" }}>Type</TableCell>
                <TableCell sx={headerCellStyle}>Subject</TableCell>
                {levelFilter === "mail_level" && (
                  <TableCell sx={headerCellStyle}>Mail ID</TableCell>
                )}
                {levelFilter === "mail_level" && (
                  <TableCell sx={headerCellStyle}>
                    <TableSortLabel
                      active={orderBy === "sent_at"}
                      direction={orderBy === "sent_at" ? order : "asc"}
                      onClick={() => handleSort("sent_at")}
                      sx={sortLabelStyle}
                    >
                      Sent At
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell sx={headerCellStyle}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleSort("status")}
                    sx={sortLabelStyle}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                {levelFilter === "mail_level" && (
                  <TableCell sx={headerCellStyle}>Mail State</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={levelFilter === "mail_level" ? 8 : 5}
                    align="center"
                    sx={{ py: 4, color: "#94a3b8" }}
                  >
                    {search || typeFilter !== "all"
                      ? "No matching records found"
                      : "No mail tracking data available"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#f0f4ff" },
                    }}
                  >
                    <TableCell sx={bodyCellStyle}>
                      {row.unique_id ? (
                        <span
                          style={{ color: "#1e40af", cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => window.open(`/hospitals/${row.unique_id}/details`, "_blank")}
                        >
                          {row.unique_id}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell sx={bodyCellStyle}>{row.client_name || "-"}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, minWidth: "120px" }}>{getTypeBadge(row.mail_type)}</TableCell>
                    <TableCell sx={{ ...bodyCellStyle, maxWidth: "250px", whiteSpace: "normal", wordBreak: "break-word" }}>{row.subject || "-"}</TableCell>
                    {levelFilter === "mail_level" && (
                      <TableCell sx={bodyCellStyle}>{row.mail_id || "-"}</TableCell>
                    )}
                    {levelFilter === "mail_level" && (
                      <TableCell sx={bodyCellStyle}>{formatDate(row.sent_at)}</TableCell>
                    )}
                    <TableCell sx={bodyCellStyle}>{getStatusText(row.status)}</TableCell>
                    {levelFilter === "mail_level" && (
                      <TableCell sx={bodyCellStyle}>{row.state || "-"}</TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && activeData.length > 0 && (
        <TablePagination
          component="div"
          count={activeData.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      )}

    </div>
  );
}

const headerCellStyle = {
  fontWeight: 700,
  fontSize: "13px",
  color: "#ffffff",
  backgroundColor: "#3949ab",
  borderBottom: "none",
  whiteSpace: "nowrap",
  padding: "14px 16px",
};

const sortLabelStyle = {
  color: "#ffffff !important",
  "&.MuiTableSortLabel-root": {
    color: "#ffffff",
  },
  "&.MuiTableSortLabel-root:hover": {
    color: "#e0e0e0",
  },
  "&.Mui-active": {
    color: "#ffffff !important",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#ffffff !important",
  },
};

const bodyCellStyle = {
  fontSize: "12px",
  color: "#1e293b",
  borderBottom: "1px solid #e8eaf0",
  padding: "12px 16px",
};

export default MailTracker;
